/**
 * Design Redux Slice
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only manages design state
 * - Open/Closed: Easy to add new actions without modifying existing reducers
 * 
 * Redux Toolkit Best Practices:
 * - Uses createSlice for automatic action creators
 * - Uses createAsyncThunk for async operations
 * - Immutable updates with Immer (built into RTK)
 * - Proper TypeScript typing
 * 
 * State Management Pattern:
 * - Loading states (idle, pending, succeeded, failed)
 * - Error handling
 * - Normalized state for better performance
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getApiErrorMessage } from '../utils/errorHandling';
import { designService } from '../services/api/design.service';
import {
  IDesign,
  ICreateDesignDto,
  IUpdateDesignDto,
  DesignStatus,
} from '../types/design.types';

// ============================================
// STATE INTERFACE
// ============================================

export interface DesignState {
  // Normalized design storage (by ID for O(1) lookups)
  designs: Record<string, IDesign>;
  designIds: string[]; // Ordered list of IDs
  
  // Deleted designs (Recently Deleted)
  deletedDesigns: any[];
  
  // Currently selected/active design
  currentDesignId: string | null;
  
  // Pagination metadata
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  
  // Loading states (follows Redux convention)
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  
  // Individual operation loading states (for granular UI control)
  operations: {
    creating: boolean;
    updating: boolean;
    deleting: boolean;
    fetching: boolean;
    fetchingDeleted: boolean;
    restoring: boolean;
  };
}

const initialState: DesignState = {
  designs: {},
  designIds: [],
  deletedDesigns: [],
  currentDesignId: null,
  pagination: null,
  status: 'idle',
  error: null,
  operations: {
    creating: false,
    updating: false,
    deleting: false,
    fetching: false,
    fetchingDeleted: false,
    restoring: false,
  },
};

// ============================================
// ASYNC THUNKS
// ============================================

/**
 * Create a new design
 * Usage: dispatch(createDesign(payload))
 */
export const createDesign = createAsyncThunk(
  'designs/create',
  async (payload: ICreateDesignDto, { rejectWithValue }) => {
    try {
      const design = await designService.createDesign(payload);
      return design;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to create design')
      );
    }
  }
);

/**
 * Fetch all user designs
 * Usage: dispatch(fetchUserDesigns({ page: 1, limit: 20 }))
 */
export const fetchUserDesigns = createAsyncThunk(
  'designs/fetchAll',
  async (params?: { page?: number; limit?: number; summary?: boolean }, { rejectWithValue }) => {
    try {
      const result = await designService.getUserDesigns(params);
      // Handle both array and paginated response
      if (Array.isArray(result)) {
        return { designs: result, pagination: null };
      }
      return result;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to fetch designs')
      );
    }
  }
);

/**
 * Fetch a single design by ID
 * Usage: dispatch(fetchDesignById('uuid'))
 */
export const fetchDesignById = createAsyncThunk(
  'designs/fetchById',
  async (designId: string, { rejectWithValue }) => {
    try {
      const design = await designService.getDesignById(designId);
      return design;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to fetch design')
      );
    }
  }
);

/**
 * Update an existing design
 * Usage: dispatch(updateDesign({ id: 'uuid', updates: {...} }))
 */
export const updateDesign = createAsyncThunk(
  'designs/update',
  async (
    { id, updates }: { id: string; updates: IUpdateDesignDto },
    { rejectWithValue }
  ) => {
    try {
      const updatedDesign = await designService.updateDesign(id, updates);
      return updatedDesign;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to update design')
      );
    }
  }
);

/**
 * Delete a design (Soft Delete)
 * Usage: dispatch(deleteDesign('uuid'))
 */
export const deleteDesign = createAsyncThunk(
  'designs/delete',
  async (designId: string, { rejectWithValue }) => {
    try {
      await designService.deleteDesign(designId);
      return designId;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to delete design')
      );
    }
  }
);

/**
 * Fetch deleted designs (Recently Deleted)
 * Usage: dispatch(fetchDeletedDesigns())
 */
export const fetchDeletedDesigns = createAsyncThunk(
  'designs/fetchDeleted',
  async (_, { rejectWithValue }) => {
    try {
      const deletedDesigns = await designService.getDeletedDesigns();
      return deletedDesigns;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to fetch deleted designs')
      );
    }
  }
);

/**
 * Restore a deleted design
 * Usage: dispatch(restoreDesign('uuid'))
 */
export const restoreDesign = createAsyncThunk(
  'designs/restore',
  async (designId: string, { rejectWithValue }) => {
    try {
      const restoredDesign = await designService.restoreDesign(designId);
      return restoredDesign;
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to restore design')
      );
    }
  }
);

/**
 * Save or Update Design (Smart Save)
 * Checks if design exists in state and either updates or creates
 * Usage: dispatch(saveOrUpdateDesign(payload))
 */
export const saveOrUpdateDesign = createAsyncThunk(
  'designs/saveOrUpdate',
  async (payload: ICreateDesignDto, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { designs: DesignState };
      const currentDesignId = state.designs.currentDesignId;
      
      // 🔍 DEBUG: Log the state
      console.log('🔄 saveOrUpdateDesign - Full state:', state.designs);
      console.log('🔄 saveOrUpdateDesign - currentDesignId:', currentDesignId);
      console.log('🔄 saveOrUpdateDesign - Will update?', !!currentDesignId);
      
      // If currentDesignId exists, update the existing design
      if (currentDesignId) {
        console.log('✏️ UPDATING design with ID:', currentDesignId);
        
        const updatePayload: IUpdateDesignDto = {
          // No id field - it comes from URL parameter
          title: payload.title,
          frontSide: payload.frontSide,
          backSide: payload.backSide,
          // 🔥 CRITICAL: Include preview URLs in update (was missing!)
          frontPreviewUrl: payload.frontPreviewUrl,
          backPreviewUrl: payload.backPreviewUrl,
          // Optional fields
          ...(payload.metadata && { metadata: payload.metadata }),
        };
        
        console.log('✏️ Update payload:', updatePayload);
        console.log('🖼️ Update includes preview URLs:', {
          hasFrontPreview: !!updatePayload.frontPreviewUrl,
          hasBackPreview: !!updatePayload.backPreviewUrl,
          frontPreviewLength: updatePayload.frontPreviewUrl?.length || 0,
          backPreviewLength: updatePayload.backPreviewUrl?.length || 0,
        });
        
        const updatedDesign = await designService.updateDesign(
          currentDesignId,
          updatePayload
        );
        console.log('✅ Design updated successfully:', updatedDesign.id);
        return { design: updatedDesign, isUpdate: true };
      } else {
        // Create new design
        console.log('➕ CREATING new design');
        const newDesign = await designService.createDesign(payload);
        console.log('✅ Design created successfully:', newDesign.id);
        
        // Store design ID for guests to claim after login
        localStorage.setItem('guest_design_id', newDesign.id);
        console.log('📝 Stored guest_design_id for potential claim after login');
        
        return { design: newDesign, isUpdate: false };
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('❌ Save/Update failed:', error);
      return rejectWithValue(
        getApiErrorMessage(err, 'Failed to save design')
      );
    }
  }
);

// ============================================
// SLICE
// ============================================

const designSlice = createSlice({
  name: 'designs',
  initialState,
  reducers: {
    // Synchronous actions
    
    /**
     * Set the currently active design
     */
    setCurrentDesign: (state, action: PayloadAction<string | null>) => {
      console.log('🔧 setCurrentDesign called with:', action.payload);
      console.log('🔧 Previous currentDesignId:', state.currentDesignId);
      state.currentDesignId = action.payload;
      console.log('🔧 New currentDesignId:', state.currentDesignId);
    },

    /**
     * Clear error state
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset design state (e.g., on logout)
     */
    resetDesignState: () => initialState,

    /**
     * Add a design to state (for optimistic updates)
     */
    addDesignOptimistic: (state, action: PayloadAction<IDesign>) => {
      const design = action.payload;
      state.designs[design.id] = design;
      state.designIds.push(design.id);
    },
  },

  // ============================================
  // EXTRA REDUCERS (Handle Async Thunks)
  // ============================================
  extraReducers: (builder) => {
    // CREATE DESIGN
    builder
      .addCase(createDesign.pending, (state) => {
        state.operations.creating = true;
        state.error = null;
      })
      .addCase(createDesign.fulfilled, (state, action) => {
        state.operations.creating = false;
        const design = action.payload;
        // Add to normalized state
        state.designs[design.id] = design;
        state.designIds.push(design.id);
        state.currentDesignId = design.id;
        state.status = 'succeeded';
      })
      .addCase(createDesign.rejected, (state, action) => {
        state.operations.creating = false;
        state.error = action.payload as string;
        state.status = 'failed';
      });

    // FETCH ALL DESIGNS
    builder
      .addCase(fetchUserDesigns.pending, (state) => {
        state.operations.fetching = true;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUserDesigns.fulfilled, (state, action) => {
        state.operations.fetching = false;
        state.status = 'succeeded';
        
        // Handle both array and paginated response
        const designsArray = Array.isArray(action.payload)
          ? action.payload
          : (action.payload as any)?.designs || [];
        
        // Normalize designs
        const designs: Record<string, IDesign> = {};
        const designIds: string[] = [];
        
        designsArray.forEach((design: IDesign) => {
          designs[design.id] = design;
          designIds.push(design.id);
        });
        
        state.designs = designs;
        state.designIds = designIds;
        
        // Store pagination if available
        if (!Array.isArray(action.payload) && (action.payload as any)?.pagination) {
          state.pagination = (action.payload as any).pagination;
        } else {
          state.pagination = null;
        }
        
        // 🔥 IMPORTANT: DON'T clear currentDesignId when fetching designs
        // This was likely causing the issue - keep currentDesignId intact
        console.log('📥 Fetched designs, keeping currentDesignId:', state.currentDesignId);
      })
      .addCase(fetchUserDesigns.rejected, (state, action) => {
        state.operations.fetching = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // FETCH SINGLE DESIGN
    builder
      .addCase(fetchDesignById.pending, (state) => {
        state.operations.fetching = true;
        state.error = null;
      })
      .addCase(fetchDesignById.fulfilled, (state, action) => {
        state.operations.fetching = false;
        const design = action.payload;
        state.designs[design.id] = design;
        if (!state.designIds.includes(design.id)) {
          state.designIds.push(design.id);
        }
      })
      .addCase(fetchDesignById.rejected, (state, action) => {
        state.operations.fetching = false;
        state.error = action.payload as string;
      });

    // UPDATE DESIGN
    builder
      .addCase(updateDesign.pending, (state) => {
        state.operations.updating = true;
        state.error = null;
      })
      .addCase(updateDesign.fulfilled, (state, action) => {
        state.operations.updating = false;
        const updatedDesign = action.payload;
        state.designs[updatedDesign.id] = updatedDesign;
      })
      .addCase(updateDesign.rejected, (state, action) => {
        state.operations.updating = false;
        state.error = action.payload as string;
      });

    // DELETE DESIGN
    builder
      .addCase(deleteDesign.pending, (state) => {
        state.operations.deleting = true;
        state.error = null;
      })
      .addCase(deleteDesign.fulfilled, (state, action) => {
        state.operations.deleting = false;
        const deletedId = action.payload;
        
        // Remove from state
        delete state.designs[deletedId];
        state.designIds = state.designIds.filter((id) => id !== deletedId);
        
        // Clear current design if it was deleted
        if (state.currentDesignId === deletedId) {
          state.currentDesignId = null;
        }
      })
      .addCase(deleteDesign.rejected, (state, action) => {
        state.operations.deleting = false;
        state.error = action.payload as string;
      });

    // FETCH DELETED DESIGNS
    builder
      .addCase(fetchDeletedDesigns.pending, (state) => {
        state.operations.fetchingDeleted = true;
        state.error = null;
      })
      .addCase(fetchDeletedDesigns.fulfilled, (state, action) => {
        state.operations.fetchingDeleted = false;
        state.deletedDesigns = action.payload;
      })
      .addCase(fetchDeletedDesigns.rejected, (state, action) => {
        state.operations.fetchingDeleted = false;
        state.error = action.payload as string;
      });

    // RESTORE DESIGN
    builder
      .addCase(restoreDesign.pending, (state) => {
        state.operations.restoring = true;
        state.error = null;
      })
      .addCase(restoreDesign.fulfilled, (state, action) => {
        state.operations.restoring = false;
        const restoredDesign = action.payload;
        
        // Add back to active designs
        state.designs[restoredDesign.id] = restoredDesign;
        if (!state.designIds.includes(restoredDesign.id)) {
          state.designIds.push(restoredDesign.id);
        }
        
        // Remove from deleted designs
        state.deletedDesigns = state.deletedDesigns.filter(
          (d) => d.id !== restoredDesign.id
        );
      })
      .addCase(restoreDesign.rejected, (state, action) => {
        state.operations.restoring = false;
        state.error = action.payload as string;
      });

    // SAVE OR UPDATE DESIGN (Smart Save)
    builder
      .addCase(saveOrUpdateDesign.pending, (state) => {
        state.operations.creating = true; // Use creating flag for both
        state.operations.updating = true;
        state.error = null;
      })
      .addCase(saveOrUpdateDesign.fulfilled, (state, action) => {
        state.operations.creating = false;
        state.operations.updating = false;
        
        const { design, isUpdate } = action.payload;
        
        // Add or update in normalized state
        state.designs[design.id] = design;
        
        if (!isUpdate && !state.designIds.includes(design.id)) {
          // New design - add to list
          state.designIds.push(design.id);
        }
        
        // Set as current design
        state.currentDesignId = design.id;
        state.status = 'succeeded';
      })
      .addCase(saveOrUpdateDesign.rejected, (state, action) => {
        state.operations.creating = false;
        state.operations.updating = false;
        state.error = action.payload as string;
        state.status = 'failed';
      });
  },
});

// ============================================
// SELECTORS (Memoized for performance)
// ============================================

/**
 * Select all designs as an array
 */
export const selectAllDesigns = (state: { designs: DesignState }) =>
  state.designs.designIds.map((id) => state.designs.designs[id]);

/**
 * Select current design
 */
export const selectCurrentDesign = (state: { designs: DesignState }) =>
  state.designs.currentDesignId
    ? state.designs.designs[state.designs.currentDesignId]
    : null;

/**
 * Select current design ID (just the ID, not the object)
 */
export const selectCurrentDesignId = (state: { designs: DesignState }) =>
  state.designs.currentDesignId;

/**
 * Select design by ID
 */
export const selectDesignById = (state: { designs: DesignState }, id: string) =>
  state.designs.designs[id];

/**
 * Select loading state
 */
export const selectDesignsLoading = (state: { designs: DesignState }) =>
  state.designs.operations.fetching || state.designs.operations.creating;

/**
 * Select designs by status
 */
export const selectDesignsByStatus = (
  state: { designs: DesignState },
  status: DesignStatus
) =>
  state.designs.designIds
    .map((id) => state.designs.designs[id])
    .filter((design) => design.status === status);

// ============================================
// EXPORTS
// ============================================

export const {
  setCurrentDesign,
  clearError,
  resetDesignState,
  addDesignOptimistic,
} = designSlice.actions;

export default designSlice.reducer;

